import ErrorBanner from "components/ui/ErrorBanner";
import {
  DictionaryMode,
  DictionaryToolsContext,
  DictionaryToolsContextValue,
} from "contexts/DictionaryToolsContext";
import { ErrorBoundary } from "components/ErrorBoundary";
import TalaeraSpinner from "components/TalaeraSpinner/TalaeraSpinner";
import { PropsWithChildren, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useUser } from "contexts/UserContext";
import { useToast } from "contexts/ToastContext";
import { useAddSavedWord, personalDictionaryQuery } from "services/dictionary";
import { useQuery } from "@tanstack/react-query";
import { Link, useLocation } from "react-router-dom";
import { routes } from "pages/routes";
import { useRouteMeta } from "utils/routing";
import Tabs from "components/ui/Tabs";
import SearchInputWithSuggestions from "./SearchInputWithSuggestions/SearchInputWithSuggestions";
import DefineTab from "./DefineTab";
import Sidebar from "./Sidebar";
import TextSelectionMenu, {
  NO_SELECTION_CLASS_NAME,
} from "./TextSelectionMenu/TextSelectionMenu";
import TranslateTab from "./TranslateTab";
import { useDictionarySearch } from "./useDictionarySearch";
import "./tabs.css";
import "./DictionaryTools.css";
import type { TranslationKey } from "services/i18n/keys";

const LOOKUP_FAILED_TEXT = "Dictionary.panel.lookupFailed";

const DRAWER_MODES = ["define", "translate"] as const;

const MODE_LABEL_KEYS: Record<(typeof DRAWER_MODES)[number], TranslationKey> = {
  define: "Dictionary.panel.modeDefine",
  translate: "Dictionary.panel.modeTranslate",
};

export default function DictionaryTools(props: PropsWithChildren) {
  const { t } = useTranslation();
  const user = useUser(false);
  const enabled = useRouteMeta().tools && user?.canUseDictionaryTools === true;

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const {
    text,
    setText,
    mode,
    onModeChanged,
    onSearch,
    applyLookup,
    result,
    isError,
    isFetching,
  } = useDictionarySearch();

  const { showToast } = useToast();
  const { mutate: runAddWord } = useAddSavedWord();

  const openSidebar = useCallback(
    (openMode: DictionaryMode, selectionText: string) => {
      if (!selectionText) {
        return;
      }
      if (openMode === "save") {
        runAddWord(
          { word: selectionText },
          {
            onSuccess: added =>
              showToast(
                t(
                  added.isNew
                    ? "Dictionary.panel.savedToVocab"
                    : "Dictionary.panel.alreadyInVocab"
                )
              ),
          }
        );
        return;
      }
      setIsSidebarOpen(true);
      applyLookup(openMode, selectionText);
    },
    [applyLookup, runAddWord, showToast, t]
  );

  const location = useLocation();
  // Only total_count is read, hence the one-row page.
  const savedCountQuery = useQuery({
    ...personalDictionaryQuery({ page: 1, size: 1 }),
    enabled,
  });
  const savedCount = savedCountQuery.data?.total_count;

  const closeSidebar = useCallback(() => setIsSidebarOpen(false), []);

  const toggleSidebar = useCallback(() => setIsSidebarOpen(prev => !prev), []);

  const contextValue = useMemo<DictionaryToolsContextValue>(
    () => ({
      enabled,
      isSidebarOpen,
      openSidebar,
      toggleSidebar,
      closeSidebar,
    }),
    [enabled, isSidebarOpen, openSidebar, toggleSidebar, closeSidebar]
  );

  const defineContent = isError ? t(LOOKUP_FAILED_TEXT) : result;

  const activeTab =
    mode === "define" ? (
      <DefineTab defineContent={defineContent} />
    ) : (
      <TranslateTab searchText={text} />
    );

  const sidebarPanel = (
    <ErrorBoundary
      fallback={<ErrorBanner>{t("Dictionary.panel.genericError")}</ErrorBanner>}
    >
      <div className="dictionary-tools-panel">
        <div className="dictionary-header">
          <h2>{t("Dictionary.panel.title")}</h2>
          <button
            type="button"
            className="close-action"
            onClick={closeSidebar}
            aria-label={t("Common.actions.close")}
          >
            ✕
          </button>
        </div>

        <div className="dictionary-content">
          <div className={NO_SELECTION_CLASS_NAME}>
            <SearchInputWithSuggestions
              value={text}
              onValueChanged={setText}
              onSearch={onSearch}
            />
          </div>
          <div className="dictionary-tools-result-panel">
            <Tabs
              className="dictionary-mode-tabs"
              aria-label={t("Dictionary.panel.title")}
              items={DRAWER_MODES.map(m => ({
                value: m,
                label: t(MODE_LABEL_KEYS[m]),
              }))}
              value={mode}
              onChange={value => onModeChanged(value as (typeof DRAWER_MODES)[number])}
            />
            {isFetching && (
              <div className="spinner-container">
                <TalaeraSpinner />
              </div>
            )}
            {!isFetching && activeTab}
          </div>
        </div>

        {!location.pathname.startsWith(routes.vocabulary.path()) && (
          <div className="dictionary-footer">
            <Link to={routes.vocabulary.path()} onClick={closeSidebar}>
              {t("Dictionary.footer.myVocabulary")}
              <span aria-hidden> →</span>
            </Link>
            {savedCount !== undefined && (
              <span className="dictionary-footer-count">
                {t("Dictionary.footer.wordTotal", {
                  total: savedCount,
                  count: savedCount,
                })}
              </span>
            )}
          </div>
        )}
      </div>
    </ErrorBoundary>
  );

  return (
    <DictionaryToolsContext.Provider value={contextValue}>
      <Sidebar enabled={enabled} isOpen={isSidebarOpen} sidebarContent={sidebarPanel}>
        {props.children}
      </Sidebar>
      {enabled && <TextSelectionMenu />}
    </DictionaryToolsContext.Provider>
  );
}
